import { computed, getCurrentInstance, onMounted, ref, type ComponentPublicInstance } from 'vue';
import { isShenaiCompatible } from '@/feature/vital-signs/utils/browserCompatibility';

// BE returns the full legal-documents catalogue with a `category` discriminator
// per record. Filter by category + typeName + language + region and take the
// highest version to stay stable across new releases.
const CATEGORY = 'ai';
const TYPE_NAME = 'terms_and_conditions';
const DIALOG_KEY = 'vital-signs-agreement';
const ROUTE_NAME = 'vitalSigns';

/**
 * Builds the external public-page URL from the current host so sandbox /
 * staging deploys don't bounce Flutter / native users to production:
 *   - production: app.ulekare.cz         → vital-signs.ulekare.cz
 *   - staging:    app.staging.ulekare.cz → vital-signs.staging.ulekare.cz
 *   - sandbox:    app.sandbox.ulekare.cz → vital-signs.sandbox.ulekare.cz
 * On localhost the swap leaves `localhost` untouched — dev only, acceptable.
 */
export function getVitalSignsExternalUrl(bearer: string): string {
  const host = window.location.hostname.replace(/^app\./, 'vital-signs.');
  return `https://${host}/?access=${encodeURIComponent(bearer)}`;
}

type LegalDocument = {
  id: number | string;
  category: string;
  typeName: string;
  languageCode: string;
  regionCode: string;
  version: number;
};

type UserLegalDocument = {
  legalDocumentId: number | string;
  confirmedAt?: string;
};

type StoreWithHttp = {
  getters: Record<string, unknown>;
  commit: CallableFunction;
  dispatch: CallableFunction;
  $http: { get: CallableFunction; post: CallableFunction };
  $authReady?: Promise<unknown>;
};

type VmProxy = ComponentPublicInstance & {
  $t: CallableFunction;
  $i18n: { locale: string };
  $store: StoreWithHttp;
  $router: { push: CallableFunction };
  $domainRegion: 'cz' | 'sk' | 'hu' | undefined;
  $localePath: CallableFunction;
  $auth?: {
    user?: {
      legalDocuments?: UserLegalDocument[];
      services?: Record<string, string | undefined>;
      benefit?: { active?: boolean };
    };
  };
  gtmPush?: CallableFunction;
};

export function useVitalSignsConsent() {
  const vm = getCurrentInstance()?.proxy as unknown as VmProxy;
  const store = vm.$store;

  // B2B-only feature. The rest of the app uses `$auth.user.benefit.active` as
  // the canonical B2B/employer indicator (see `mixins/dashboardBoxes.js`:
  // `videoCallAvailable`, `assistLineAvailable`, etc.). It's part of the user
  // payload returned by `fetchUser`, so it's synchronously available after
  // login — no race with the lazy `partnerData` fetch.
  const isB2B = computed(() => Boolean(vm.$auth?.user?.benefit?.active));

  const vitalSignsAvailable = computed(() => {
    // Service flag must be present and not explicitly inactive. Treating an
    // undefined flag as "available" would expose the feature to users whose
    // BE record predates the rollout.
    const serviceFlag = vm.$auth?.user?.services?.vitalSigns;
    return (
      vm.$domainRegion === 'cz' &&
      vm.$i18n.locale === 'cs' &&
      serviceFlag !== undefined &&
      serviceFlag !== 'inactive' &&
      isB2B.value
    );
  });

  // Browser support is a separate signal from `vitalSignsAvailable`. We want
  // an unavailable user (wrong region / B2C) silently redirected, but an
  // available-but-incompatible user (B2B on iOS 16 / iPhone X) to see an
  // explicit "browser unsupported" message — silent redirect would just
  // confuse them when their colleague's device works fine.
  const vitalSignsCompatible = computed(() => isShenaiCompatible());

  // 'visible' = service is shown but disabled (user can see it but can't use it yet).
  const vitalSignsRestricted = computed(() => vm.$auth?.user?.services?.vitalSigns === 'visible');

  // Local ref — the Vuex mutation reassigns a nested property which Vue 2's
  // reactivity can't observe, so we keep the resolved document here and
  // update it explicitly after each fetch.
  const vitalSignsDocument = ref<LegalDocument | undefined>(undefined);

  function pickLatest(docs: LegalDocument[] | undefined): LegalDocument | undefined {
    const candidates = docs?.filter(
      doc =>
        doc.category === CATEGORY &&
        doc.typeName === TYPE_NAME &&
        doc.languageCode.toLowerCase() === vm.$i18n.locale &&
        doc.regionCode.toLowerCase() === vm.$domainRegion
    );
    if (!candidates?.length) {
      return undefined;
    }
    return candidates.reduce((best, curr) => (curr.version > best.version ? curr : best));
  }

  function readFromCache(): LegalDocument | undefined {
    const byCategory = store.getters['legalDocuments/legalDocuments'] as Record<
      string,
      LegalDocument[] | undefined
    >;
    return pickLatest(byCategory[CATEGORY]);
  }

  const hasVitalSignsConsent = computed(() => {
    const documentId = vitalSignsDocument.value?.id;
    if (!documentId) {
      return false;
    }
    const userDocuments: UserLegalDocument[] = vm.$auth?.user?.legalDocuments ?? [];
    return userDocuments.some(doc => doc.legalDocumentId === documentId);
  });

  async function fetchVitalSignsDocument() {
    const locale = vm.$i18n.locale;
    try {
      const data = (await store.$http.get(
        `/v3/legal-documents?category=${CATEGORY}&languageCode=${locale}`
      )) as LegalDocument[];
      store.dispatch('legalDocuments/setLegalDocuments', { category: CATEGORY, data });
      vitalSignsDocument.value = pickLatest(data);
    } catch (error) {
      store.dispatch('errorModal/handleError', { error, showModal: false, code: '0x00000060' });
    }
  }

  async function handleVitalSignsClick() {
    if (vitalSignsRestricted.value) {
      return;
    }

    const isInFlutterWebView = !!(window as unknown as { flutter_inappwebview?: unknown })
      .flutter_inappwebview;
    if (isInFlutterWebView) {
      const bearer = (store as unknown as { state: { auth: { accessToken: string } } }).state.auth
        .accessToken;
      window.open(getVitalSignsExternalUrl(bearer), '_blank');
      return;
    }

    // Wait for both auth hydration and the latest legal-document fetch before
    // deciding. Without this, a fast tap on a freshly-logged-in (or freshly-
    // mounted) dashboard sees `vitalSignsDocument.value === undefined` or an
    // empty `legalDocuments` array — both make `hasVitalSignsConsent` false,
    // and the dialog pops up for a user who already signed.
    if (store.$authReady) {
      await store.$authReady;
    }
    if (!vitalSignsDocument.value) {
      await fetchVitalSignsDocument();
    }
    if (hasVitalSignsConsent.value) {
      vm.$router.push(vm.$localePath(ROUTE_NAME));
      return;
    }
    store.commit('profile/dashboard/setDialog', DIALOG_KEY);
  }

  const vitalSignsService = computed(() => ({
    icon: 'vital-signs',
    badge: String(vm.$t('vitalSigns.boxBadge')),
    variant: 'vital-signs',
    title: String(vm.$t('vitalSigns.boxTitle')),
    subtitle: String(vm.$t('vitalSigns.boxSubtitle')),
    text: String(vm.$t('vitalSigns.boxText')),
    dataCy: 'vitalSignsBox',
    buttons: [
      {
        type: 'block',
        disabled: vitalSignsRestricted.value,
        text: String(vm.$t('vitalSigns.boxButton')),
        clickAction: () => {
          vm.gtmPush?.({
            event: 'button_click',
            'button.type': String(vm.$t('vitalSigns.boxButton'))
          });
          handleVitalSignsClick();
        }
      }
    ]
  }));

  onMounted(() => {
    // Seed from store cache (may have been populated by another consumer).
    vitalSignsDocument.value = readFromCache();
    if (vitalSignsAvailable.value && !vitalSignsDocument.value) {
      fetchVitalSignsDocument();
    }
  });

  return {
    vitalSignsAvailable,
    vitalSignsRestricted,
    vitalSignsCompatible,
    vitalSignsDocument,
    hasVitalSignsConsent,
    vitalSignsService,
    handleVitalSignsClick,
    fetchVitalSignsDocument
  };
}

export const VITAL_SIGNS_DIALOG_KEY = DIALOG_KEY;
export const VITAL_SIGNS_CATEGORY = CATEGORY;
export const VITAL_SIGNS_TYPE_NAME = TYPE_NAME;
